import React, { useContext } from 'react'
import styled, { css, ThemeContext } from 'styled-components'

// Components
import Text from '../Text'
import { RowAligned } from '../layout/LayoutAtoms'
import Icon from '../icon/Icon'

// Styles
import {
  shadows,
  fastTransition,
  gradientBackground,
} from '../../styles/effects'
import { contrastColorOn, hsla } from '../../styles/colors'
import { mqFrom } from '../../styles/mediaQueries'

// Types
import { IconType } from '../icon/IconType'
import { TextStyleType, TextTagType } from '../../common/models/typography'

interface IProps {
  children: React.ReactNode
}

interface ITitleCardProps {
  className?: string
  title: string
  icon?: IconType
  children: React.ReactNode
}

/**
 * [FrameworkCore]
 * Set styles for a generic card
 */
export const applyGenericCardStyles = () => {
  return css`
    padding: 24px;
    transition: background-color ${fastTransition};
    border-radius: 4px;
    background-color: ${({ theme }) => theme.colors.lightest};
    box-shadow: ${shadows.depthTwo};
  `
}

export const roundedCardStyles = () => {
  return css`
    border-radius: 12px;
    background-color: ${({ theme }) => theme.colors.lightest};
    box-shadow: ${shadows.depthTwo};
    transition: all ${fastTransition};
  `
}

export const RoundedCard: React.FC<{
  className?: string
  children: React.ReactNode
  onClick?: React.MouseEventHandler<HTMLDivElement>
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>
  // Cypress handle, forwarded to the card element.
  'data-cy'?: string
}> = (props) => <RoundedContainer {...props}>{props.children}</RoundedContainer>

export const CardWithTitle: React.FC<ITitleCardProps> = ({
  title,
  children,
  icon,
  className,
}) => {
  const theme = useContext(ThemeContext)
  return (
    <Container className={className}>
      <Header>
        {icon && <Icon type={icon} color={theme.colors.primary} size={24} />}
        <Text textStyle="displayS" tag="h3">
          {title}
        </Text>
      </Header>
      <Content>{children}</Content>
    </Container>
  )
}

export const ClickCard: React.FC<{
  title: string
  caption: string
  titleStyle?: TextStyleType
  captionStyle?: TextStyleType
  // Heading tag by default so the title picks up the brand heading font on the
  // public Employer Branding pages; pass 'p' to render it in the body font.
  titleTag?: TextTagType
  icon?: IconType
  emoji?: string
  // Optional tint for the icon block (e.g. a department's colour)
  iconColor?: string
  // Optional image faded into the card background (e.g. a department's image)
  image?: string
  // Keep the icon block a rounded square even with an emoji (emoji defaults to round)
  squareIcon?: boolean
  // Pinned to the top-right corner, opposite the icon block (e.g. a count tag)
  badge?: React.ReactNode
  onClick?: React.MouseEventHandler<HTMLDivElement>
  selected?: boolean
  className?: string
  // Rendered under the caption, for a card that needs a footer (e.g. a progress bar)
  children?: React.ReactNode
}> = ({
  title,
  caption,
  titleStyle = 'labelHeavy',
  captionStyle = 'bodySmall',
  titleTag = 'h3',
  icon,
  emoji,
  iconColor,
  image,
  squareIcon,
  badge,
  onClick,
  selected,
  className,
  children,
}) => {
  const theme = useContext(ThemeContext)
  // Only the fallback SVG icon on a custom tint needs contrast handling: keep it
  // legible (white on dark, dark on light). Emojis carry their own colour.
  const iconFill =
    icon && iconColor
      ? contrastColorOn(iconColor, theme.colors.textDark, theme.colors.lightest)
      : undefined

  return (
    <ClickCardContainer
      onClick={onClick}
      className={className}
      selected={selected}
      iconColor={iconColor}
      iconFill={iconFill}
      image={image}
    >
      {badge && <BadgeSlot>{badge}</BadgeSlot>}
      {(icon || emoji || iconColor) && (
        <IconContainer
          round={!!emoji && !squareIcon}
          tint={iconColor}
          iconFill={iconFill}
        >
          {icon && <Icon size={16} type={icon} />}
          {emoji && <Text textStyle="title">{emoji}</Text>}
        </IconContainer>
      )}
      <RowAligned>
        <ClickCardTitle tag={titleTag} textStyle={titleStyle} marginRight={8}>
          {title}
        </ClickCardTitle>
        {!icon && !emoji && !iconColor && (
          <Icon size={12} type="ChevronRight" />
        )}
      </RowAligned>
      <Text textStyle={captionStyle} color="text72" marginTop={8}>
        {caption}
      </Text>
      {children}
    </ClickCardContainer>
  )
}

const BadgeSlot = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
`

export const IconContainer = styled.div<{
  round?: boolean
  tint?: string
  iconFill?: string
}>`
  background-color: ${({ tint, theme }) => tint || theme.colors.primaryLight};
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.textLightest};
  margin-bottom: 16px;
  border-radius: ${({ round }) => (round ? '50%' : '8px')};
  transition: background-color ${fastTransition};

  // && outranks the parent card's generic "svg > path { fill: text72 }" so a
  // tinted fallback icon shows its contrast colour in the base (non-hover) state.
  && svg > path {
    fill: ${({ iconFill, theme }) => iconFill || theme.colors.text72};
  }
`

const ClickCardTitle = styled(Text)`
  text-wrap: auto;
`

const ClickCardContainer = styled.div<{
  selected?: boolean
  iconColor?: string
  iconFill?: string
  image?: string
}>`
  ${roundedCardStyles()};
  position: relative;
  padding: 16px;
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.textLightest};
  transition: all ${fastTransition};
  // With an image, fade it diagonally into the card bg (most white in the
  // bottom-left where the text sits, image revealed toward the top-right). The
  // alpha ramps down gently from the start so there's no hard edge, and the
  // colour only shows through the (opaque) icon container on top.
  background: ${({ image, theme }) =>
    image
      ? `linear-gradient(to top right,
          ${theme.colors.lightest} 0%,
          ${hsla(theme.colors.lightest, 0.9)} 50%,
          ${hsla(theme.colors.lightest, 0.2)} 100%),
         url("${image}") center / cover`
      : theme.colors.lightest};

  svg > path {
    fill: ${({ theme }) => theme.colors.text72};
    transition: fill ${fastTransition};
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primaryFadeLight};
    box-shadow: ${shadows.depthThree};
    ${({ image, theme }) =>
      !image && `background: ${gradientBackground(theme)};`}

    ${IconContainer} {
      background-color: ${({ iconColor, theme }) =>
        iconColor || theme.colors.primaryFadeLight};

      // A tinted fallback icon keeps its contrast colour (the tile bg stays the
      // tint on hover, so primary would be illegible).
      svg > path {
        fill: ${({ iconFill, theme }) => iconFill || theme.colors.primary};
      }
    }

    svg > path {
      fill: ${({ theme }) => theme.colors.primary};
    }

    ${ClickCardTitle} {
      color: ${({ theme }) => theme.colors.primary};
    }
  }

  ${({ selected, iconColor, iconFill, image, theme }) =>
    selected &&
    `
      border-color: ${theme.colors.primary};
      ${!image ? `background: ${gradientBackground(theme)};` : ''}

      ${IconContainer} {
        background-color: ${iconColor || theme.colors.primaryFadeLight};

        svg > path {
          fill: ${iconFill || theme.colors.primary};
        }
      }

      svg > path {
        fill: ${theme.colors.primary};
      }

      ${ClickCardTitle} {
        color: ${theme.colors.primary};
      }
    `}
`

const GenericCard = (props: IProps) => {
  return <StyledGenericCard>{props.children}</StyledGenericCard>
}

export const StyledGenericCard = styled.div`
  ${applyGenericCardStyles()}
`

const Container = styled.div`
  ${roundedCardStyles()};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const RoundedContainer = styled.div`
  ${roundedCardStyles()};
  padding: 16px;

  ${mqFrom.M`
    padding: 24px;
  `}
`

const Header = styled.header`
  padding: 16px 16px 4px;
  display: flex;
  align-items: center;
`

const Content = styled.div`
  padding: 0 16px;

  ${mqFrom.M`
    display: flex;
    flex-direction: column;
    height: 100%;
  `}
`

export default GenericCard
